import * as angular from 'angular';
import * as _ from 'lodash';

import { Miniature } from '../models/miniature';
import { IWidgetConfiguration, IWidget } from './models';
import { IRootScope } from 'IRootScope';
import {
    IInputValuesService,
    IExportToCsvService,
    ISeriesSelectorService,
    IDateRangeService,
    IwidgetIntervalHelperService,
    ILoggerService,
    ITableByGroupService
} from '../services';
import {
    AlarmsDisabledTableWidget,
    AlarmsEvolutionWidget,
    AlarmsGaugesWidget,
    AlarmsHeatMapWidget,
    AlarmsTableWidget,
    ApprovalTableWidget,
    badValuesTableWidget,
    BusinessKpiWidget,
    DigitalSignatureWidget,
    ChartByIdentifierWidget,
    connectionPointWidget,
    DashboardThumbnailWidget,
    DoughnutChartWidget,
    FlowChartWidget,
    GaugeChartWidget,
    GroupStatesWidget,
    GroupsTreeWidget,
    LogbookTableWidget,
    ManualInputsWidget,
    MapWidget,
    percentageOfGoodValuesEvolutionWidget,
    percentageOfGoodValuesWidget,
    PivotTableWidget,
    ReconciliationMeasureWidget,
    ScatterWidget,
    SeriesSelectionWidget,
    TableByGroupWidget,
    TableWidget,
    TagsTrendWidget,
    TextWidget,
    TileWidget,
    ValidatedValueSelectionWidget
} from './widgets.index';
import { createQualityGaugeWidget } from './createQualityGaugeWidget';
import { createQualityBadActorsWidget } from './createQualityBadActorsWidget';
import { createQualityPercentageEvolutionWidget } from './createQualityPercentageEvolutionWidget';
import { ButtonWidget } from './button/button.widget';
import { AlarmsTableConfiguration } from './alarmsTable/alarmsTable.configuration';
import { SpotManualInputsWidget } from './spotManualInputs/spotManualInputs.widget';
import { TableByGroupWidgetConfiguration } from './tableByGroup';
import { SpotValuesWidget } from './spotValuesWidget/spotValuesWidget.widget';

export interface IWidgetFactory {
    createWidget(type: string, config?: IWidgetConfiguration): IWidget;
    widgetList(): Miniature[];
    getMiniature(type: string): Miniature;
    register(mini: Miniature): void;
}

export class WidgetFactory implements IWidgetFactory {
    static serviceId = 'widgetFactory';

    private _widgetlist = [];
    private _initialized: boolean;

    static $inject = [
        '$rootScope',
        '$q',
        '$translate',
        'inputValuesService',
        'exportToCsvService',
        'seriesSelectorService',
        'dateRangeService',
        'widgetIntervalHelperService',
        'loggerService',
        'tableByGroupService'
    ];

    constructor(
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _$translate: ng.translate.ITranslateService,
        private _inputValuesService: IInputValuesService,
        private _exportToCsvService: IExportToCsvService,
        private _seriesSelectorService: ISeriesSelectorService,
        private _dateRangeService: IDateRangeService,
        private _intervalHelper: IwidgetIntervalHelperService,
        private _loggerService: ILoggerService,
        private _tableByGroupService: ITableByGroupService
    ) {
        this._widgetlist = [];
    }

    createWidget(type: string, config?: IWidgetConfiguration): IWidget {
        let widget: IWidget = null;
        try {
            widget = createQualityGaugeWidget(type, config);
            if (widget === null) {
                widget = createQualityBadActorsWidget(type, config);
            }
            if (widget === null) {
                widget = createQualityPercentageEvolutionWidget(type, config);
            }
            if (widget === null) {
                switch (type) {
                    // not converted yet
                    case 'badValuesTable':
                        widget = new badValuesTableWidget(this._$q, this._exportToCsvService, config);
                        break;
                    case 'percentageOfGoodValuesEvolution':
                        widget = new percentageOfGoodValuesEvolutionWidget(this._inputValuesService, this._exportToCsvService, config);
                        break;
                    case 'percentageOfGoodValuesGauge':
                        widget = new percentageOfGoodValuesWidget(this._$rootScope, this._$q, this._inputValuesService, config);
                        break;
                    case 'AlarmsDisabledTable':
                        widget = new AlarmsDisabledTableWidget();
                        break;
                    case 'AlarmsEvolution':
                        widget = new AlarmsEvolutionWidget();
                        break;
                    case 'AlarmsGauges':
                        widget = new AlarmsGaugesWidget();
                        break;
                    case 'AlarmsHeatMap':
                        widget = new AlarmsHeatMapWidget();
                        break;
                    case 'AlarmsTable':
                        const atw = new AlarmsTableWidget();
                        atw.configuration.showNumberOfExcursions = config ? !!(config as AlarmsTableConfiguration).showNumberOfExcursions : true;
                        widget = atw;
                        break;
                    case 'approvalTable':
                        widget = new ApprovalTableWidget();
                        break;
                    case 'businessKpi':
                        widget = new BusinessKpiWidget(this._$q, this._intervalHelper, this._dateRangeService);
                        break;
                    case 'chartByIdentifier':
                        widget = new ChartByIdentifierWidget(this._$translate, this._$q, this._intervalHelper);
                        break;
                    case 'connectionPoint':
                        widget = new connectionPointWidget(this._$q);
                        break;
                    case 'DoughnutChart':
                        widget = new DoughnutChartWidget(this._$translate, this._$q, this._intervalHelper);
                        break;
                    case 'DigitalSignature':
                        widget = new DigitalSignatureWidget();
                        break;
                    case 'dashboardThumbnail':
                        widget = new DashboardThumbnailWidget();
                        break;
                    case 'FlowChart':
                        widget = new FlowChartWidget(this._$q, this._intervalHelper);
                        break;
                    case 'gaugeChart':
                        widget = new GaugeChartWidget(this._$q, this._$translate, this._intervalHelper);
                        break;
                    case 'GroupStates':
                        widget = new GroupStatesWidget(this._intervalHelper);
                        break;
                    case 'groupsTree':
                        widget = new GroupsTreeWidget();
                        break;
                    case 'LogbookTable':
                        widget = new LogbookTableWidget(this._$q, this._intervalHelper);
                        break;
                    case 'ManualInputs':
                        widget = new ManualInputsWidget();
                        break;
                    case 'ReconciliationMeasure':
                        widget = new ReconciliationMeasureWidget();
                        break;
                    case 'scatter':
                        widget = new ScatterWidget(this._$q, this._intervalHelper);
                        break;
                    case 'seriesSelection':
                        widget = new SeriesSelectionWidget(this._seriesSelectorService);
                        break;
                    case 'table':
                        widget = new TableWidget(this._$q, this._dateRangeService, this._intervalHelper);
                        break;
                    case 'TableByGroup':
                        const tbgw = new TableByGroupWidget(this._tableByGroupService, this._intervalHelper);
                        if (config) {
                            // #46621 : fallback for existing widget. Default value is set to false in constructor
                            const cfg = config as TableByGroupWidgetConfiguration;
                            tbgw.configuration.hasUnits =
                                cfg.hasUnits ||
                                (config as TableByGroupWidgetConfiguration).hasUnits === undefined ||
                                (config as TableByGroupWidgetConfiguration).hasUnits === null;
                            // #46831 : fallback if there is a config (=> existing widget) and not saved
                            if (cfg.isSearchByRelativePath === null || cfg.isSearchByRelativePath === undefined) {
                                tbgw.configuration.isSearchByRelativePath = true;
                            }
                        }
                        widget = tbgw;
                        break;
                    case 'tagsTrend':
                        widget = new TagsTrendWidget(this._$q, this._$translate, this._intervalHelper);
                        break;
                    case 'text':
                        widget = new TextWidget();
                        break;
                    case 'Tile':
                        widget = new TileWidget(this._$q, this._intervalHelper);
                        break;
                    case 'defaultGroupValidation': // for retro-compat
                    case 'ValidatedValueSelection':
                        widget = new ValidatedValueSelectionWidget();
                        break;
                    case 'button':
                        widget = new ButtonWidget();
                        break;
                    case 'pivotTable':
                        widget = new PivotTableWidget(this._intervalHelper);
                        break;
                    case 'SpotManualInputs':
                        widget = new SpotManualInputsWidget();
                        break;
                    case 'spotValuesWidget':
                        widget = new SpotValuesWidget(this._$q, this._intervalHelper);
                        break;
                    case 'Map':
                        widget = new MapWidget(this._intervalHelper);
                        break;
                    default:
                        widget = this.createPlaceholderWidget(type, config);
                        break;
                }
            }
            if (widget !== null) {
                const translatedTitle = this._$translate.instant('Title');
                if (widget.name) {
                    const translatedName = this._$translate.instant(widget.name);
                    widget.name = translatedName;
                }
                if (widget.configuration?.options?.title) {
                    widget.configuration.options.title = translatedTitle;
                }
                widget.showBottomMargin = true;
                widget.showTopMargin = true;
                widget.showLeftMargin = true;
                widget.showRightMargin = true;
            }
        } catch (e) {
            console.error(e);
            this._loggerService.logError(e.message);
        }

        return widget;
    }

    private createPlaceholderWidget(type: string, config?: any): IWidget {
        const widget: any = {
            name: config?.name || type,
            type: type,
            baseType: 'placeholder',
            sizeX: config?.sizeX || 6,
            sizeY: config?.sizeY || 4,
            col: config?.col || 0,
            row: config?.row || 0,
            guid: config?.guid,
            backgroundColor: config?.backgroundColor,
            hideTitle: config?.hideTitle,
            isTitleHidden: config?.isTitleHidden,
            dashboardLink: config?.dashboardLink,
            configuration: {
                message: 'Widget not supported in V2',
                widgetType: type,
                ...config?.configuration
            },
            // Flag to identify placeholder widgets
            isPlaceholder: true,
            ...config
        };
        return widget;
    }

    widgetList(): Miniature[] {
        if (!this._initialized) {
            this._initialized = true;
        }
        return this._widgetlist;
    }

    getMiniature(type: string): Miniature {
        let found = _.find(this.widgetList(), (w) => w.type === type);
        if (!found) {
            const w = this.createWidget(type);
            found = {
                group: '',
                type: type,
                title: '',
                highlights: w.highlights,
                icon: w.icon,
                sortingOrder: 0
            };
        }
        return found;
    }

    register(mini: Miniature): void {
        this._widgetlist.push(mini);
    }
}
